import React, { useEffect, useState } from 'react';

import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { Container } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import { changeCurrencyCode, emptyCart, setBusinessId, setCustomer } from '~/actions/cart';

import UserBreadcrumbs from '~/components/Common/Elements/UserBreadcrumbs';
import ErrorDisplay from '~/components/Common/ErrorDisplay';
import Spinner from '~/components/Common/Spinner';
import FraudWarning from '~/components/Users/History/FraudWarning';

import { getEmployee } from '~/services/BusinessTraveller/BusinessTravellerService';
import UsersService from '~/services/UsersService';

import NewOrderPage from './NewOrderPage';

type MatchParams = { id_user: string; step: string };

type Props = {
  doChangeCurrencyCode: Utils.ActionDispatcher<typeof changeCurrencyCode>;
  doEmptyCart: Utils.ActionDispatcher<typeof emptyCart>;
  doSetCustomer: Utils.ActionDispatcher<typeof setCustomer>;
  doSetBusinessId: Utils.ActionDispatcher<typeof setBusinessId>;
  orderType: App.OrderType;
};

function NewOrderPageContainer({
  orderType = 'offer',
  doChangeCurrencyCode,
  doEmptyCart,
  doSetCustomer,
  doSetBusinessId,
}: Props) {
  const [loadingState, setLoadingState] = useState<Utils.FetchingState>('loading');
  const [error, setError] = useState<string | null>(null); // Page level error, prevents entire page to work properly

  const { id_user: userId, step } = useParams<MatchParams>();

  const brand = useSelector((state: App.State) => state.tenant.brand);
  const customer = useSelector((state: App.State) => state.cart.customer);

  const isLoading = loadingState == 'loading';

  useEffect(() => {
    doEmptyCart();

    setLoadingState('loading');

    UsersService.getUser(userId, { brand })
      .then(async (user: App.User) => {
        const region = libRegions.getRegionByCode(user.country_code, brand);

        doChangeCurrencyCode(region.currencyCode, region.code);
        doSetCustomer(user);

        if (brand === 'lebusinesstraveller') {
          const employee = await getEmployee(user.id_member);
          if (employee) {
            doSetBusinessId(employee.businessId);
          }
        }

        setLoadingState('success');
      })
      .catch((err) => {
        setError(err.message);
        setLoadingState('failed');
      });
  }, [brand, userId, doChangeCurrencyCode, doEmptyCart, doSetCustomer, doSetBusinessId]);

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Container>
        <ErrorDisplay message={error} />
      </Container>
    );
  }

  const showFraudWarning = step === 'offer-selection' || step === 'gift-card-selection';

  return (
    <>
      <Container>
        <UserBreadcrumbs
          userId={userId}
          user={customer ? { label: customer.fullName || customer.email || 'User', href: `/users/${userId}` } : null}
          currentPage="New order"
        />
        {showFraudWarning && <FraudWarning userId={userId} />}
      </Container>
      <NewOrderPage user={customer} onEmptyCart={doEmptyCart} orderType={orderType} step={step} />
    </>
  );
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      doEmptyCart: emptyCart,
      doSetCustomer: setCustomer,
      doSetBusinessId: setBusinessId,
      doChangeCurrencyCode: changeCurrencyCode,
    },
    dispatch,
  );
}

export default connect(null, mapDispatchToProps)(NewOrderPageContainer);
